import React, {useState, useMemo, useEffect} from "react";
import {Divider, Card, Typography} from "antd";
import MobileReport from "./MobileReport";
import OldReports from "./OldReports";
import s from "../mobile.module.css";
import moment from 'moment';
import PlanProps from "./PlanProps";
import ObjectProps from "./ObjectProps";

const {Text} = Typography;

// Основной компонент для отображения мобильного списка планов работ
const MobilePlansList = ({
                             plans,           // Массив планов работ
                             objects,         // Массив объектов
                             staff,           // Массив сотрудников
                             reports,         // Массив отчетов
                             reportsTemp,     // Временные данные отчетов
                             photoLinks,      // Ссылки на фотографии объектов
                             oldreports,      // Архивные отчеты
                             onSubmit,        // Функция отправки отчета
                             user             // Данные текущего пользователя
                         }) => {
    // Состояние для управления текущим видом (план, отчет, архив)
    const [currentView, setCurrentView] = useState('plan');

    // Состояние для хранения выбранного объекта
    const [selectedObject, setSelectedObject] = useState(null);

    // Состояние для хранения выбранного отчета
    const [selectedReport, setSelectedReport] = useState(null);

    // Состояние для хранения отчетов за сегодня
    const [todayReports, setTodayReports] = useState([]);

    // Эффект для фильтрации сегодняшних отчетов
    useEffect(() => {
        if (reports && reports.length > 0) {
            // Получаем начало текущего дня в формате Unix timestamp
            const today = moment().startOf('day').format('X');

            // Фильтруем отчеты, оставляя только созданные сегодня
            const todayReports = reports.filter(report =>
                Number(report.time_plan) >= Number(today)
            );
            setTodayReports(todayReports);
        }
    }, [reports]); // Зависимость - reports обновляется при изменении отчетов

    // Константы для типов доступа к объектам
    const ACCESS_TYPES = {
        1: {label: 'Открытый', color: 'green'},
        2: {label: 'Ключ у нас', color: 'blue'},
        3: {label: 'Ключ на объекте', color: 'orange'},
        4: {label: 'Через контактное лицо', color: 'purple'}
    };

    // Мемоизированный расчет текущей команды пользователя
    const currentTeam = useMemo(() => {
        console.log(user.id);
        console.log(plans);

        // Проверяем наличие данных о планах
        if (!plans.length || !plans[0].props) return null;

        // Ищем команду, где текущий пользователь является лидером
        return plans[0].props.find(team => Number(team.lead) === Number(user.id));
    }, [plans, user.id]); // Пересчитывается при изменении планов или ID пользователя

    // Функция форматирования даты из timestamp
    const formatDate = (timestamp) => {
        return moment(Number(timestamp) * 1000).format('DD.MM.YYYY');
    };

    // Функция получения имени сотрудника по ID
    const getStaffName = (staffId) => {
        const staffMember = staff.find(s => Number(s.id) === Number(staffId));
        return staffMember ? `${staffMember.firstname} ${staffMember.lastname}` : 'Неизвестно';
    };

    // Функция получения детальной информации об объекте
    const getObjectDetails = (objectId, index) => {
        // Находим объект в массиве
        const object = objects.find(obj => Number(obj.id) === Number(objectId));
        if (!object) return null;

        // Разбиваем комментарии команды (разделитель - `)
        const teamComments = currentTeam?.comment?.split('`') || [];

        return {
            ...object,
            // Добавляем информацию о доступе
            access: ACCESS_TYPES[object.access] || {label: 'Нет данных', color: 'default'},
            // Берем комментарий соответствующий индексу объекта
            comment: teamComments[index] || '',
            // Форматируем дату последнего обслуживания
            formattedLastService: object.date_last_service
                ? moment(Number(object.date_last_service) * 1000).format('DD.MM.YYYY')
                : ''
        };
    };

    // Обработчик добавления нового отчета
    const handleAddReport = (objectId, objectName, todayReport = null) => {
        // Фильтруем фотографии для выбранного объекта
        const objectPhotos = photoLinks.filter(p => Number(p.object_id) === Number(objectId));
        setSelectedObject({id: objectId, name: objectName, photos: objectPhotos});

        // Если передан отчет за сегодня - редактируем его
        if (todayReport) {
            setSelectedReport(todayReport);
        }

        // Переключаемся на вид отчетов
        setCurrentView('report');
    };

    // Обработчик просмотра архивных отчетов
    const handleViewOldReports = async (objectId, objectName) => {
        setSelectedObject({id: objectId, name: objectName});
        setCurrentView('old-reports');
    };

    // Обработчик редактирования существующего отчета
    const handleEditReport = (report) => {
        // Находим объект отчета
        const object = objects.find(obj => Number(obj.id) === Number(report.object_id));

        // Фильтруем фотографии объекта
        const objectPhotos = photoLinks.filter(p => Number(p.object_id) === Number(report.object_id));

        // Устанавливаем данные для редактирования
        setSelectedReport(report);
        setSelectedObject({
            id: report.object_id,
            name: object ? `${object.name} ${object.long_name || ''}` : '',
            photos: objectPhotos
        });
        setCurrentView('report');
    };

    // Функция рендеринга списка объектов
    const renderObjects = () => {
        // Проверяем наличие объектов у команды
        if (!currentTeam?.objects_id) return null;

        // Разбиваем строку с ID объектов и рендерим каждый
        return currentTeam.objects_id.split(',').map((objectId, index) => {
            const object = getObjectDetails(objectId, index);
            if (!object) return null;

            // Проверяем наличие отчета за сегодня для этого объекта
            const todayReport = todayReports.find(report =>
                (Number(report.object_id) === Number(objectId) && Number(report.owner) === Number(user.id))
            );

            return (
                <ObjectProps
                    object={object}
                    key={index}
                    handleAddReport={handleAddReport}
                    handleViewOldReports={handleViewOldReports}
                    todayReport={todayReport}
                    hasTodayReport={!!todayReport}
                />
            );
        });
    };

    // Рендер компонента отчета
    if (currentView === 'report') {
        return (
            <MobileReport
                object={selectedObject}
                report={selectedReport}
                onClose={() => setCurrentView('plan')}
                onSubmit={onSubmit}
                reportsTemp={reportsTemp}
                plan={plans[0]}
            />
        );
    }

    // Рендер компонента архивных отчетов
    if (currentView === 'old-reports') {
        return (
            <OldReports
                object={selectedObject}
                reports={oldreports}
                reportsTemp={reportsTemp}
                onClose={() => setCurrentView('plan')}
                onEditReport={handleEditReport}
            />
        );
    }

    // Сообщение при отсутствии активной команды
    if (!currentTeam) {
        return (
            <div className={s.noAction}>
                <Text type="secondary">🎯 Нет активных задач на текущий день</Text>
            </div>
        );
    }

    // Основной рендер списка планов
    return (
        <div className={s.planContainer}>
            <Card
                title={
                    <div>
                        <div style={{textAlign: 'center', width: '100%'}}>
                            📅 План работ #{plans[0].id}
                        </div>
                        <div style={{textAlign: 'right', width: '100%'}}>
                            <Text type="secondary">{formatDate(plans[0]?.used_date)}</Text>
                        </div>
                    </div>
                }
                className={s.planCard}
            >
                {/* Компонент свойств плана */}
                <PlanProps currentTeam={currentTeam} getStaffName={getStaffName} plans={plans}/>
                <Divider>🏢 Объекты</Divider>
                {/* Рендер списка объектов */}
                {renderObjects()}
            </Card>
        </div>
    );
};

export default MobilePlansList;